/**
 * Phase 3.28 — Sub-Sections von BusinessDataSection.
 * SteuerdatenSection, AccountSection, BankSection, BusinessDocSection.
 */
import { useState, useRef } from "react";
import { Receipt, Mail, Lock, Landmark, FileText, Phone, AtSign, ShieldCheck, ShieldQuestion, ShieldOff, ExternalLink, Eye, CheckCircle2, Clock, UploadCloud, ScanLine, AlertTriangle, Loader2, Check } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import SaveStatusIndicator from "./SaveStatusIndicator";
import { useAutosave } from "../../hooks/useAutosave";
import { useLockedFields } from "../../hooks/useLockedFields";

const LockedHint = ({ show }) => show ? (
    <div className="flex items-start gap-2 rounded-sm border border-gray-200 bg-gray-50 p-2.5 mb-3" data-testid="locked-by-hq-hint">
        <Lock className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
        <p className="text-[11px] text-gray-600">Vom <strong>Hauptsitz</strong> zentral vorgegeben — hier nur sichtbar, nicht bearbeitbar.</p>
    </div>
) : null;

export function SteuerdatenSection({ currentUser, embedded = false }) {
    const { refresh } = useAuth();
    const { isLocked, lockProps, anyLocked } = useLockedFields();
    const [form, setForm] = useState({
        tax_id: currentUser.tax_id || "",
        tax_number: currentUser.tax_number || "",
        vat_id: currentUser.vat_id || "",
        tax_advisor_email: currentUser.tax_advisor_email || "",
        datev_consultant_number: currentUser.datev_consultant_number || "",
        datev_client_number: currentUser.datev_client_number || "",
        datev_auto_send_enabled: currentUser.datev_auto_send_enabled || false,
    });
    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    // Etappe 3 (Global Autosave) — Steuerdaten speichern automatisch.
    const { saving, dirty, savedAt } = useAutosave(form, (f) => api.patch("/users/me/dealer-profile", f));
    const wrap = embedded ? "" : "bg-white border border-gray-200 rounded-sm p-5";
    return (
        <div className={wrap} data-testid="steuerdaten-section">
            {!embedded && (
                <div className="flex items-center gap-2 mb-3">
                    <Receipt className="w-5 h-5 text-[#FFCC00]" />
                    <h3 className="font-display font-bold text-lg">Steuerdaten & DATEV</h3>
                </div>
            )}
            <p className="text-xs text-gray-600 mb-3">
                Steuernummer, USt-IdNr. und Steuer-ID werden für Rechnungen und das Impressum verwendet. Der Handelsregister-Eintrag wird unter „Gewerbeanmeldung / Handelsregisterauszug" gepflegt.
            </p>
            <LockedHint show={anyLocked && (isLocked("tax_number") || isLocked("vat_id") || isLocked("tax_id"))} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Steuernummer</Label>
                    <Input value={form.tax_number} onChange={(e) => update("tax_number", e.target.value)} {...lockProps("tax_number", "rounded-sm mt-1 font-mono-num")} placeholder="z.B. 151/815/08151" data-testid="dealer-tax-number" />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">USt-IdNr. (MwSt)</Label>
                    <Input value={form.vat_id} onChange={(e) => update("vat_id", e.target.value)} {...lockProps("vat_id", "rounded-sm mt-1 font-mono-num")} placeholder="z.B. DE123456789" data-testid="dealer-vat-id" />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Steuer-ID</Label>
                    <Input value={form.tax_id} onChange={(e) => update("tax_id", e.target.value)} {...lockProps("tax_id", "rounded-sm mt-1 font-mono-num")} placeholder="z.B. 12 345 678 901" data-testid="dealer-tax-id" />
                </div>
            </div>

            {/* DATEV-Auto-Versand Block */}
            <div className="mt-5 pt-4 border-t border-gray-200">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">Steuerberater & DATEV-Versand</div>
                <p className="text-xs text-gray-500 mb-3">
                    E-Mail-Adresse des Steuerberaters für DATEV-Belegtransfer. Mit aktivem Auto-Versand erhält der Steuerberater monatlich automatisch das Belegtransfer-ZIP des Vormonats (versendet am 1. eines Monats).
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-3">
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Steuerberater E-Mail</Label>
                        <Input
                            type="email"
                            value={form.tax_advisor_email}
                            onChange={(e) => update("tax_advisor_email", e.target.value)}
                            className="rounded-sm mt-1"
                            placeholder="steuerberater@kanzlei.de"
                            data-testid="dealer-tax-advisor-email"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">DATEV-Beraternr.</Label>
                        <Input
                            value={form.datev_consultant_number}
                            onChange={(e) => update("datev_consultant_number", e.target.value)}
                            className="rounded-sm mt-1 font-mono-num"
                            placeholder="z.B. 123456"
                            maxLength="20"
                            data-testid="dealer-datev-consultant"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Mandantennr.</Label>
                        <Input
                            value={form.datev_client_number}
                            onChange={(e) => update("datev_client_number", e.target.value)}
                            className="rounded-sm mt-1 font-mono-num"
                            placeholder="z.B. 54321"
                            maxLength="20"
                            data-testid="dealer-datev-client"
                        />
                    </div>
                    <div className="sm:col-span-1 flex items-end">
                        <label className={`flex items-center gap-2 cursor-pointer text-sm ${!form.tax_advisor_email ? "opacity-50" : ""}`}>
                            <input
                                type="checkbox"
                                checked={!!form.datev_auto_send_enabled}
                                onChange={(e) => update("datev_auto_send_enabled", e.target.checked)}
                                disabled={!form.tax_advisor_email}
                                data-testid="dealer-datev-auto-send"
                            />
                            <span><strong>Monatlicher Auto-Versand</strong></span>
                        </label>
                    </div>
                </div>
                {form.datev_auto_send_enabled && (
                    <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm p-2 flex items-start gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>
                            <strong>Automatischer Versand aktiv.</strong> Am 1. jedes Monats wird das DATEV-Belegtransfer-ZIP
                            des Vormonats automatisch an <strong>{form.tax_advisor_email || "—"}</strong> versendet
                            (idempotent — jede Periode wird höchstens einmal versendet).
                        </span>
                    </div>
                )}
                {currentUser.datev_last_sent_at && (
                    <div className="mt-2 text-[11px] text-gray-600">
                        Letzter Versand: <strong>{new Date(currentUser.datev_last_sent_at).toLocaleString("de-DE")}</strong>
                        {currentUser.datev_last_sent_to && <span> an {currentUser.datev_last_sent_to}</span>}
                        {currentUser.datev_last_sent_count && <span> ({currentUser.datev_last_sent_count} Rechnungen)</span>}
                        {currentUser.datev_last_sent_source && <span> · {currentUser.datev_last_sent_source}</span>}
                    </div>
                )}
            </div>

            <div className="mt-4 flex justify-end" data-testid="steuerdaten-save-status">
                <SaveStatusIndicator saving={saving} dirty={dirty} savedAt={savedAt} />
            </div>
        </div>
    );
}


// ============================================================================
// ACCOUNT (Email + Password change) — B2B only
// ============================================================================
export function AccountSection({ currentUser, embedded = false }) {
    const { refresh } = useAuth();
    const [emailForm, setEmailForm] = useState({ current_password: "", new_email: currentUser.email });
    const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [busyE, setBusyE] = useState(false);
    const [busyP, setBusyP] = useState(false);

    const submitEmail = async () => {
        if (!emailForm.current_password || !emailForm.new_email) {
            toast.error("Aktuelles Passwort und neue E-Mail erforderlich");
            return;
        }
        if (emailForm.new_email === currentUser.email) {
            toast.error("Neue E-Mail ist identisch mit der aktuellen");
            return;
        }
        setBusyE(true);
        try {
            await api.post("/users/me/change-email", emailForm);
            await refresh();
            setEmailForm({ current_password: "", new_email: emailForm.new_email });
            toast.success("E-Mail geändert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Änderung fehlgeschlagen");
        } finally {
            setBusyE(false);
        }
    };

    const submitPw = async () => {
        if (pwForm.new_password.length < 6) {
            toast.error("Neues Passwort muss mind. 6 Zeichen haben");
            return;
        }
        if (pwForm.new_password !== pwForm.confirm) {
            toast.error("Passwörter stimmen nicht überein");
            return;
        }
        setBusyP(true);
        try {
            await api.post("/users/me/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
            setPwForm({ current_password: "", new_password: "", confirm: "" });
            toast.success("Passwort geändert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Änderung fehlgeschlagen");
        } finally {
            setBusyP(false);
        }
    };

    return (
        <div className={embedded ? "" : "bg-white border border-gray-200 rounded-sm p-6"} data-testid="account-section">
            <div className="flex items-center gap-2 mb-1">
                <Lock className="w-5 h-5 text-[#FFCC00]" />
                <h3 className="font-display font-bold text-lg">Konto</h3>
            </div>
            <p className="text-xs text-gray-600 mb-5">Aktuelle E-Mail: <strong className="font-mono-num" data-testid="account-current-email">{currentUser.email}</strong>
                {currentUser.ansprechpartner_employee_id && !currentUser.ansprechpartner_same_as_gf && (
                    <span className="block mt-1 text-[11px] text-gray-500" data-testid="account-email-linked-hint">
                        Mit dem ausgewählten Ansprechpartner{currentUser.ansprechpartner_name ? ` (${currentUser.ansprechpartner_name})` : ""} verknüpft — die Login-E-Mail folgt automatisch, wenn der Ansprechpartner wechselt.
                    </span>
                )}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Email */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-sm">
                    <div className="flex items-center gap-2 mb-3">
                        <AtSign className="w-4 h-4 text-gray-500" />
                        <h4 className="font-bold text-sm uppercase tracking-wider text-gray-700">E-Mail ändern</h4>
                    </div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Neue E-Mail</Label>
                    <Input type="email" value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} className="rounded-sm mt-1 mb-3" data-testid="new-email-input" />
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Aktuelles Passwort</Label>
                    <Input type="password" value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} className="rounded-sm mt-1 mb-3" data-testid="email-current-pw-input" />
                    <Button onClick={submitEmail} disabled={busyE} className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm" data-testid="change-email-btn">
                        {busyE ? "Speichert…" : "E-Mail ändern"}
                    </Button>
                </div>
                {/* Passwort */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-sm">
                    <div className="flex items-center gap-2 mb-3">
                        <Lock className="w-4 h-4 text-gray-500" />
                        <h4 className="font-bold text-sm uppercase tracking-wider text-gray-700">Passwort ändern</h4>
                    </div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Aktuelles Passwort</Label>
                    <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className="rounded-sm mt-1 mb-3" data-testid="pw-current-input" />
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Neues Passwort (mind. 6 Zeichen)</Label>
                    <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className="rounded-sm mt-1 mb-3" data-testid="pw-new-input" />
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Neues Passwort bestätigen</Label>
                    <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="rounded-sm mt-1 mb-3" data-testid="pw-confirm-input" />
                    <Button onClick={submitPw} disabled={busyP} className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm" data-testid="change-pw-btn">
                        {busyP ? "Speichert…" : "Passwort ändern"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// BANK + DOCUMENTS — extends DealerProfileSection (rendered as separate cards)
// ============================================================================
export function BankSection({ currentUser, embedded = false }) {
    const { refresh } = useAuth();
    const { isLocked, lockProps, anyLocked } = useLockedFields();
    const [form, setForm] = useState({
        bank_iban: currentUser.bank_iban || "",
        bank_bic: currentUser.bank_bic || "",
        bank_account_holder: currentUser.bank_account_holder || currentUser.company_name || "",
        bank_name: currentUser.bank_name || "",
        sepa_direct_debit_enabled: !!currentUser.sepa_direct_debit_enabled,
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/users/me/dealer-profile", form);
            await refresh();
            toast.success("Zahlungsdaten gespeichert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={embedded ? "" : "bg-white border border-gray-200 rounded-sm p-6"} data-testid="bank-section">
            <div className="flex items-center gap-2 mb-1">
                <Landmark className="w-5 h-5 text-[#FFCC00]" />
                <h3 className="font-display font-bold text-lg">Zahlungsdaten</h3>
            </div>
            <p className="text-xs text-gray-600 mb-4">Auszahlungen aus Treuhand-Verkäufen erfolgen auf dieses Konto.</p>
            <LockedHint show={anyLocked && (isLocked("bank_iban") || isLocked("bank_bic") || isLocked("bank_name") || isLocked("bank_account_holder"))} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">IBAN</Label>
                    <Input value={form.bank_iban} onChange={(e) => setForm({ ...form, bank_iban: e.target.value.toUpperCase() })} placeholder="DE89 3704 0044 0532 0130 00" {...lockProps("bank_iban", "rounded-sm mt-1 font-mono-num")} data-testid="bank-iban-input" />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">BIC</Label>
                    <Input value={form.bank_bic} onChange={(e) => setForm({ ...form, bank_bic: e.target.value.toUpperCase() })} placeholder="COBADEFFXXX" {...lockProps("bank_bic", "rounded-sm mt-1 font-mono-num")} data-testid="bank-bic-input" />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Bank</Label>
                    <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="z.B. Commerzbank" {...lockProps("bank_name", "rounded-sm mt-1")} data-testid="bank-name-input" />
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Kontoinhaber</Label>
                    <Input value={form.bank_account_holder} onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value })} {...lockProps("bank_account_holder", "rounded-sm mt-1")} data-testid="bank-holder-input" />
                </div>
                <div className="sm:col-span-2">
                    <label className="flex items-start gap-2 cursor-pointer bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="sepa-direct-debit-wrap">
                        <input
                            type="checkbox"
                            checked={!!form.sepa_direct_debit_enabled}
                            onChange={(e) => setForm({ ...form, sepa_direct_debit_enabled: e.target.checked })}
                            className="mt-0.5 w-4 h-4 accent-[#111827]"
                            data-testid="sepa-direct-debit-checkbox"
                        />
                        <div>
                            <div className="font-bold text-sm">Am SEPA-Lastschriftverfahren teilnehmen</div>
                            <div className="text-[11px] text-gray-600">Mit der Aktivierung erteilen Sie ein SEPA-Lastschriftmandat — Plattform-Gebühren werden automatisch von oben genannter IBAN eingezogen. Sie erhalten 5 Tage vor Einzug eine Vorabankündigung.</div>
                        </div>
                    </label>
                </div>
            </div>
            <Button onClick={save} disabled={saving} className="mt-4 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider w-full" data-testid="save-bank-btn">
                {saving ? "Speichert…" : "Zahlungsdaten speichern"}
            </Button>
        </div>
    );
}

// Jul-2026 (User-Wunsch) — Wiederverwendbarer Dokument-Slot mit Drag&Drop-Upload,
// OCR-Prüfung (Gemini) und VIES-USt-IdNr.-Live-Abgleich. Zwei Instanzen:
// Handelsregisterauszug (business_doc_url) + Gewerbeanmeldung (gewerbeanmeldung_url).
function DocSlot({ docKey, docType, title, hint, url, refresh, onApplyReg }) {
    const apiBase = `${process.env.REACT_APP_BACKEND_URL}`;
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const [ocrBusy, setOcrBusy] = useState(false);
    const [ocr, setOcr] = useState(null);
    const [applied, setApplied] = useState({});

    const upload = async (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { toast.error("Datei zu groß (max. 10 MB)"); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.patch("/users/me/dealer-profile", { [docKey]: `${apiBase}${data.url}` });
            await refresh();
            toast.success(`${title} hochgeladen`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
        } finally {
            setUploading(false);
        }
    };
    const remove = async () => {
        try {
            await api.patch("/users/me/dealer-profile", { [docKey]: "" });
            await refresh();
            setOcr(null);
            toast.success("Dokument entfernt");
        } catch {
            toast.error("Entfernen fehlgeschlagen");
        }
    };
    const onDrop = (e) => { e.preventDefault(); setDragOver(false); if (!uploading) upload(e.dataTransfer?.files?.[0]); };
    const runOcr = async () => {
        setOcrBusy(true);
        try {
            const { data } = await api.post(`/users/me/verify-doc-ocr?doc=${docType}`);
            setOcr(data);
            setApplied({});
            if (data.mocked) toast.info("OCR-Demomodus (LLM-Budget aktuell nicht verfügbar) — Beispielwerte.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "OCR-Prüfung fehlgeschlagen");
        } finally {
            setOcrBusy(false);
        }
    };
    const applyField = async (row) => {
        try {
            await api.patch("/users/me/dealer-profile", { [row.master_field]: row.extracted });
            if (row.master_field === "register_number" || row.master_field === "register_court") onApplyReg?.(row.master_field, row.extracted);
            await refresh();
            setApplied((p) => ({ ...p, [row.field]: true }));
            toast.success(`${row.label} übernommen`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Übernehmen fehlgeschlagen");
        }
    };
    const vies = ocr?.vies;

    return (
        <div data-testid={`doc-slot-${docType}`}>
            <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">{title}</div>
            <p className="text-[11px] text-gray-500 mb-2">{hint}</p>
            {url ? (
                <div className="flex items-center justify-between gap-3 p-3 bg-green-50 border border-green-200 rounded-sm">
                    <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-bold text-green-800 hover:underline flex-1 min-w-0" data-testid={`doc-link-${docType}`}>
                        <CheckCircle2 className="w-5 h-5 shrink-0" />
                        <span className="truncate">Dokument hochgeladen — ansehen</span>
                        <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                    <Button onClick={remove} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 rounded-sm h-8 text-xs" data-testid={`doc-remove-${docType}`}>
                        Ersetzen
                    </Button>
                </div>
            ) : (
                <div
                    role="button"
                    tabIndex={0}
                    onClick={() => !uploading && fileInputRef.current?.click()}
                    onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !uploading) { e.preventDefault(); fileInputRef.current?.click(); } }}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    aria-label={`${title} hochladen — Datei hierher ziehen oder klicken`}
                    className={`flex flex-col items-center justify-center gap-2 w-full rounded-sm border-2 border-dashed px-4 py-7 text-center cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-[#FFCC00] ${dragOver ? "border-[#FFCC00] bg-[#FFFBEB]" : "border-gray-300 bg-gray-50 hover:border-[#FFCC00] hover:bg-[#FFFBEB]/50"} ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                    data-testid={`doc-dropzone-${docType}`}
                >
                    <UploadCloud className={`w-6 h-6 ${dragOver ? "text-[#FFCC00]" : "text-gray-400"}`} />
                    <div className="text-sm font-bold text-[#111827]">{uploading ? "Wird hochgeladen…" : "Datei hierher ziehen oder klicken"}</div>
                    <div className="text-[11px] text-gray-500">PDF, JPG oder PNG · max. 10 MB</div>
                    <input ref={fileInputRef} type="file" accept="application/pdf,image/*" onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} className="hidden" data-testid={`doc-upload-input-${docType}`} />
                </div>
            )}

            {url && (
                <div className="mt-2.5" data-testid={`ocr-verify-block-${docType}`}>
                    <Button type="button" onClick={runOcr} disabled={ocrBusy} variant="outline" className="rounded-sm font-bold uppercase tracking-wider text-xs border-[#111827]/20 hover:border-[#111827]" data-testid={`ocr-verify-btn-${docType}`}>
                        {ocrBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ScanLine className="w-4 h-4 mr-1.5" />}
                        Dokument prüfen (OCR)
                    </Button>
                    <p className="text-[11px] text-gray-500 mt-1.5">Liest Firmenname, HRB, Registergericht &amp; USt-IdNr. aus dem Dokument, prüft die USt-IdNr. live gegen VIES und gleicht alles mit deinen Stammdaten ab — Übernahme pro Feld.</p>

                    {ocr && (
                        <div className="mt-2.5 border border-gray-200 rounded-sm overflow-hidden" data-testid={`ocr-result-panel-${docType}`}>
                            {ocr.mocked && (
                                <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-800 flex items-center gap-1.5" data-testid={`ocr-mock-notice-${docType}`}>
                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Demomodus: LLM-Budget aktuell nicht verfügbar — es werden Beispielwerte angezeigt.
                                </div>
                            )}
                            {vies && (
                                <div className={`px-3 py-2 border-b text-[11px] flex items-center gap-1.5 ${vies.valid === true ? "bg-emerald-50 border-emerald-200 text-emerald-800" : vies.valid === false ? "bg-red-50 border-red-200 text-red-800" : "bg-gray-50 border-gray-200 text-gray-600"}`} data-testid={`ocr-vies-${docType}`}>
                                    {vies.valid === true ? <ShieldCheck className="w-3.5 h-3.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
                                    {vies.valid === true
                                        ? <span>USt-IdNr. {vies.country_code}{vies.vat_number} ist bei VIES <b>gültig</b>{vies.name ? ` — ${vies.name}` : ""}.</span>
                                        : vies.valid === false
                                            ? <span>USt-IdNr. {vies.country_code}{vies.vat_number} ist bei VIES <b>ungültig</b>.</span>
                                            : <span>VIES-Prüfung nicht möglich{vies.error ? `: ${vies.error}` : ""}.</span>}
                                </div>
                            )}
                            <ul className="divide-y divide-gray-100">
                                {ocr.comparison.map((row) => {
                                    const isApplied = applied[row.field];
                                    const ok = row.status === "match";
                                    const canApply = (row.status === "mismatch" || row.status === "missing_master") && row.extracted && !isApplied;
                                    return (
                                        <li key={row.field} className="flex items-center gap-3 px-3 py-2.5" data-testid={`ocr-row-${docType}-${row.field}`}>
                                            {ok || isApplied
                                                ? <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                                                : row.status === "not_found"
                                                    ? <AlertTriangle className="w-4 h-4 text-gray-400 shrink-0" />
                                                    : <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />}
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{row.label}</div>
                                                <div className="text-sm text-[#111827] truncate">
                                                    {row.extracted || <span className="text-gray-400 italic">im Dokument nicht gefunden</span>}
                                                </div>
                                                {row.status === "mismatch" && (
                                                    <div className="text-[11px] text-amber-700">Stammdaten aktuell: <span className="font-mono-num">{row.current}</span></div>
                                                )}
                                            </div>
                                            {ok && <span className="text-[11px] font-bold text-emerald-700 shrink-0">Übereinstimmung</span>}
                                            {isApplied && <span className="text-[11px] font-bold text-emerald-700 shrink-0">übernommen</span>}
                                            {canApply && (
                                                <Button type="button" onClick={() => applyField(row)} className="h-7 rounded-sm bg-[#111827] hover:bg-black text-white text-[11px] font-bold shrink-0" data-testid={`ocr-apply-${docType}-${row.field}`}>
                                                    Übernehmen
                                                </Button>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


export function BusinessDocSection({ currentUser, embedded = false }) {
    const { refresh } = useAuth();
    const { isLocked, lockProps, anyLocked } = useLockedFields();
    const [requesting, setRequesting] = useState(false);

    // Jun-2026 — Handelsregister & Geschäftsführung (Impressumspflicht).
    const [reg, setReg] = useState({
        register_court: currentUser.register_court || "",
        register_number: currentUser.register_number || "",
        company_seat: currentUser.company_seat || "",
        managing_director: currentUser.managing_director || "",
    });
    const updReg = (k, v) => setReg((p) => ({ ...p, [k]: v }));
    // Jul-2026 (User-Wunsch) — Handelsregister-Daten speichern automatisch (Autosave).
    const { saving: regSaving, dirty: regDirty, savedAt: regSavedAt } = useAutosave(
        reg, (f) => api.patch("/users/me/dealer-profile", f));

    const status = currentUser.verification_status || "none";
    const hasAnyDoc = !!(currentUser.business_doc_url || currentUser.gewerbeanmeldung_url);
    const canRequest = hasAnyDoc && (status === "none" || status === "rejected");

    const requestVerification = async () => {
        setRequesting(true);
        try {
            await api.post("/users/me/request-verification");
            await refresh();
            toast.success("Verifizierungsanfrage eingereicht — wir melden uns binnen 48 h.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Anfrage fehlgeschlagen");
        } finally {
            setRequesting(false);
        }
    };

    const onApplyReg = (field, value) => setReg((p) => ({ ...p, [field]: value }));

    return (
        <div className={embedded ? "" : "bg-white border border-gray-200 rounded-sm p-6"} data-testid="business-doc-section">
            <div className="flex items-center gap-2 mb-1">
                <FileText className="w-5 h-5 text-[#FFCC00]" />
                <h3 className="font-display font-bold text-lg">Gewerbeanmeldung / Handelsregisterauszug</h3>
            </div>
            <p className="text-xs text-gray-600 mb-4">PDF, JPG oder PNG, max. 10 MB. Wird zur Verifizierung Ihres Händler-Status verwendet.</p>

            {/* Handelsregister & Geschäftsführung (Impressumspflicht) */}
            <div className="mb-5 pb-5 border-b border-gray-200" data-testid="handelsregister-block">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">Handelsregister & Geschäftsführung</div>
                <p className="text-xs text-gray-500 mb-3">
                    Handelsregisternummer, Sitz der Gesellschaft und Geschäftsführer — werden automatisch ins Impressum übernommen.
                </p>
                <LockedHint show={anyLocked && (isLocked("register_number") || isLocked("register_court") || isLocked("company_seat") || isLocked("managing_director"))} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Handelsregisternummer</Label>
                        <Input value={reg.register_number} onChange={(e) => updReg("register_number", e.target.value)} {...lockProps("register_number", "rounded-sm mt-1 font-mono-num")} placeholder="z.B. HRB 12345" data-testid="dealer-register-number" />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Amtsgericht / Registergericht</Label>
                        <Input value={reg.register_court} onChange={(e) => updReg("register_court", e.target.value)} {...lockProps("register_court", "rounded-sm mt-1")} placeholder="z.B. Amtsgericht Nürnberg" data-testid="dealer-register-court" />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Sitz der Gesellschaft</Label>
                        <Input value={reg.company_seat} onChange={(e) => updReg("company_seat", e.target.value)} {...lockProps("company_seat", "rounded-sm mt-1")} placeholder="z.B. Nürnberg" data-testid="dealer-company-seat" />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Geschäftsführer</Label>
                        <Input value={reg.managing_director} onChange={(e) => updReg("managing_director", e.target.value)} {...lockProps("managing_director", "rounded-sm mt-1")} placeholder="z.B. Max Mustermann (mehrere mit Komma trennen)" data-testid="dealer-managing-director" />
                    </div>
                </div>
                <div className="mt-3 flex justify-end" data-testid="handelsregister-save-status">
                    <SaveStatusIndicator saving={regSaving} dirty={regDirty} savedAt={regSavedAt} />
                </div>
            </div>

            {/* Verification status banner */}
            {status === "pending" && (
                <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="verification-status-pending">
                    <Clock className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
                    <div className="text-xs text-amber-900">
                        <div className="font-bold">Ihre Verifizierung wird geprüft</div>
                        <div className="mt-0.5">Unser Team prüft Ihre Unterlagen. Sie erhalten in der Regel binnen 48 h eine Rückmeldung.</div>
                    </div>
                </div>
            )}
            {status === "approved" && (
                <div className="mb-4 flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-sm" data-testid="verification-status-approved">
                    <ShieldCheck className="w-4 h-4 mt-0.5 text-green-700 shrink-0" />
                    <div className="text-xs text-green-900">
                        <div className="font-bold">Ihr Konto ist verifiziert ✓</div>
                        <div className="mt-0.5">Auf Ihrem Profil und allen Inseraten wird das grüne Verifiziert-Siegel angezeigt.</div>
                    </div>
                </div>
            )}
            {status === "rejected" && (
                <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-sm" data-testid="verification-status-rejected">
                    <ShieldOff className="w-4 h-4 mt-0.5 text-red-700 shrink-0" />
                    <div className="text-xs text-red-900">
                        <div className="font-bold">Verifizierung abgelehnt</div>
                        {currentUser.verification_note && (
                            <div className="mt-0.5 italic" data-testid="verification-note">„{currentUser.verification_note}"</div>
                        )}
                        <div className="mt-1">Bitte korrigieren Sie das Dokument und reichen Sie die Anfrage erneut ein.</div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="business-doc-slots">
                <DocSlot
                    docKey="business_doc_url"
                    docType="handelsregister"
                    title="Handelsregisterauszug"
                    hint="Aktueller HR-Auszug (Amtsgericht). PDF, JPG oder PNG · max. 10 MB."
                    url={currentUser.business_doc_url}
                    refresh={refresh}
                    onApplyReg={onApplyReg}
                />
                <DocSlot
                    docKey="gewerbeanmeldung_url"
                    docType="gewerbe"
                    title="Gewerbeanmeldung"
                    hint="Gewerbeanmeldung / Gewerbeschein. PDF, JPG oder PNG · max. 10 MB."
                    url={currentUser.gewerbeanmeldung_url}
                    refresh={refresh}
                    onApplyReg={onApplyReg}
                />
            </div>


            {canRequest && (
                <Button
                    type="button"
                    onClick={requestVerification}
                    disabled={requesting}
                    className="mt-4 bg-[#111827] hover:bg-black text-white rounded-sm font-bold uppercase tracking-wider text-xs"
                    data-testid="request-verification-btn"
                >
                    <ShieldQuestion className="w-4 h-4 mr-1" />
                    {status === "rejected" ? "Erneut anfragen" : "Verifizierung anfragen"}
                </Button>
            )}
            {!hasAnyDoc && status === "none" && (
                <p className="text-xs text-gray-500 mt-3">
                    Tipp: Laden Sie zuerst mind. ein Dokument (Handelsregisterauszug oder Gewerbeanmeldung) hoch, dann können Sie die Verifizierung anfordern.
                </p>
            )}

            {Array.isArray(currentUser.business_doc_history) && currentUser.business_doc_history.length > 0 && (
                <details className="mt-4 group" data-testid="business-doc-history">
                    <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827] flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Frühere Versionen ({currentUser.business_doc_history.length})
                    </summary>
                    <ul className="mt-2 space-y-1 pl-4">
                        {currentUser.business_doc_history.slice().reverse().map((h, i) => {
                            const archivedAt = h.archived_at ? new Date(h.archived_at).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" }) : "—";
                            return (
                                <li key={i} className="text-xs text-gray-600 flex items-center gap-2" data-testid={`business-doc-history-item-${i}`}>
                                    <span className="text-gray-400 font-mono-num">v{currentUser.business_doc_history.length - i}</span>
                                    <a href={h.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1 truncate flex-1">
                                        Dokument <ExternalLink className="w-3 h-3 shrink-0" />
                                    </a>
                                    <span className="text-gray-400 shrink-0">ersetzt am {archivedAt}</span>
                                </li>
                            );
                        })}
                    </ul>
                </details>
            )}
        </div>
    );
}

// ============================================================================
// UNTERNEHMENSDATEN — combined card wrapping Konto / Bank / Dokumente
// ============================================================================
// Phase 3.27 — BusinessDataSection in /components/dealer/BusinessDataSection.jsx ausgelagert.
export { BusinessDataSection } from "./BusinessDataSection";



